import { WebMercatorViewport } from "@deck.gl/core";
import { RealAssetType } from "../apiClient";
import { zip } from ".";

type BoundingBox = [number, number, number, number];

type ViewportInfo = {
  longitude: number;
  latitude: number;
  zoom: number;
};

interface DeploymentLike {
  external_id: string;
  bounding_box?: BoundingBox | null;
}

interface TileSpec {
  bounds: BoundingBox;
  maxZoom: number;
  bearing?: number;
  maxPitch?: number;
  width: number;
  height: number;
}

interface PickedObjectProperties {
  lng: number[];
  lat: number[];
  timestamps: Array<string | number | Date>;
}

interface PickedObject {
  properties: PickedObjectProperties;
}

export const getViewportFromBounds = (
  bounds: BoundingBox,
  width: number,
  height: number
): ViewportInfo => {
  const { longitude, latitude, zoom } = new WebMercatorViewport({
    width,
    height,
  }).fitBounds([
    [bounds[0], bounds[1]],
    [bounds[2], bounds[3]],
  ]);

  return { longitude, latitude, zoom };
};

export const calculateLineWidth = (
  zoomLevel: number,
  numOfCoordinates: number,
  assetType?: string
): number => {
  const baseLineWidth = 0.5 + 3 * (1 - zoomLevel / 20);

  const coordinateFactor = (() => {
    switch (assetType) {
      case RealAssetType.SOUND_SPEED_EQUIPMENT:
        return 4.5;

      case RealAssetType.ROV:
        return Math.max(2.75, 3.5 - Math.log10(numOfCoordinates) * 0.3);

      case RealAssetType.VESSEL:
        return Math.max(1, 2.75 - Math.log10(numOfCoordinates) * 0.4);

      case RealAssetType.SUBMERSIBLE:
        return Math.max(2.75, 3.2 - Math.log10(numOfCoordinates) * 0.25);

      default:
        return Math.max(1.5, 3 - Math.log10(numOfCoordinates) * 0.3);
    }
  })();

  const adjustedLineWidth = Math.max(2.5, baseLineWidth * coordinateFactor);

  const maxWidth = assetType === RealAssetType.SOUND_SPEED_EQUIPMENT ? 12 : 6;

  return Math.min(adjustedLineWidth, maxWidth);
};

export function calculateEncompassingBbox(
  selectedDeploymentIds: string[],
  deployments: DeploymentLike[]
): BoundingBox {
  return selectedDeploymentIds.reduce(
    ([minLng, minLat, maxLng, maxLat], id) => {
      const deployment = deployments.find((dep) => dep.external_id === id);
      if (deployment && deployment.bounding_box) {
        const [dMinLng, dMinLat, dMaxLng, dMaxLat] = deployment.bounding_box;
        return [
          Math.min(minLng, dMinLng),
          Math.min(minLat, dMinLat),
          Math.max(maxLng, dMaxLng),
          Math.max(maxLat, dMaxLat),
        ];
      }
      return [minLng, minLat, maxLng, maxLat];
    },
    [Infinity, Infinity, -Infinity, -Infinity]
  ) as BoundingBox;
}

export function calculateZoomedViewport(
  bbox: BoundingBox,
  width: number,
  height: number
): ViewportInfo & { bearing: 0; maxPitch: 89 } {
  const bufferFactor = 0.1;

  const [minLng, minLat, maxLng, maxLat] = bbox;

  const deltaLng = maxLng - minLng;
  const deltaLat = maxLat - minLat;

  const bufferLng = deltaLng * bufferFactor;
  const bufferLat = deltaLat * bufferFactor;

  const expandedBbox: BoundingBox = [
    minLng - bufferLng,
    minLat - bufferLat,
    maxLng + bufferLng,
    maxLat + bufferLat,
  ];

  expandedBbox[0] = Math.max(expandedBbox[0], -180);
  expandedBbox[1] = Math.max(expandedBbox[1], -90);
  expandedBbox[2] = Math.min(expandedBbox[2], 180);
  expandedBbox[3] = Math.min(expandedBbox[3], 90);

  const { longitude, latitude, zoom } = getViewportFromBounds(
    expandedBbox,
    width,
    height
  );

  return { longitude, latitude, zoom, bearing: 0, maxPitch: 89 };
}

export const makoTileSpecToInitialViewState = ({
  bounds,
  maxZoom,
  bearing = 0,
  maxPitch = 89,
  width,
  height,
}: TileSpec) => {
  const { longitude, latitude, zoom } = getViewportFromBounds(
    bounds,
    width,
    height
  );

  return {
    longitude,
    latitude,
    zoom,
    maxZoom,
    maxPitch,
    bearing,
  };
};

const findClosestVertexIndex = (
  hoveredPoint: [number, number],
  line: Array<[number, number]>
): number =>
  line.reduce(
    ([minDistance, closestIndex], vertex, index) => {
      const distance = Math.sqrt(
        Math.pow(vertex[0] - hoveredPoint[0], 2) +
          Math.pow(vertex[1] - hoveredPoint[1], 2)
      );
      return distance < minDistance
        ? [distance, index]
        : [minDistance, closestIndex];
    },
    [Infinity, -1]
  )[1];

export const getTimestampForSelection = (
  pickedObject: PickedObject,
  hoverCoordinate?: [number, number] | null
): string => {
  const timestamps = pickedObject.properties.timestamps;
  if (!timestamps.length) {
    return "N/A";
  }

  if (!hoverCoordinate) {
    return timestamps[0].toLocaleString();
  }

  const coordinates = zip(
    pickedObject.properties.lng,
    pickedObject.properties.lat
  ) as Array<[number, number]>;

  if (!coordinates.length) {
    return timestamps[0].toLocaleString();
  }

  const indexOfSelectedVertex = findClosestVertexIndex(
    hoverCoordinate,
    coordinates
  );
  const selectedTimestamp = timestamps[indexOfSelectedVertex] ?? timestamps[0];

  return selectedTimestamp.toLocaleString();
};

export function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }
  return result;
}
